import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { recomputeProjectTotal, recomputeUpsellAmount } from "@/hooks/use-budget-lines";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";
import { useAuth } from "@/contexts/AuthContext";
import { logProjectEvent } from "@/hooks/use-project-events";
import {
  assertBillingModeChangeAllowed,
} from "@/hooks/upsell-billing-mode-guard";
import type { UpsellBillingMode, UpsellLine } from "@/hooks/use-upsells";

export interface UpsellDraftContainerPatch {
  description?: string;
  overhead_pct?: number | null;
  discount_pct?: number;
  discount_amount?: number;
  billing_mode?: UpsellBillingMode;
}

export interface UpsellLinePersistPatch {
  description?: string;
  quantity?: number;
  unit?: string;
  quantity_hours?: number;
  amount?: number;
  estimated_cost?: number;
  cost_type?: string;
  phase?: string;
  rate_basis_hourly?: number | null;
}

export interface SaveUpsellDraftInput {
  upsell_id: string;
  project_id: string;
  containerPatch: UpsellDraftContainerPatch | null;
  /** Nye linjer (klient-id ignoreres; insert får ny uuid fra DB). */
  inserts: UpsellLine[];
  updates: Array<{ budget_line_id: string; patch: UpsellLinePersistPatch }>;
  deleteIds: string[];
}

/**
 * Batch-lagring for mersalg-editoren: container + linjer i én mutation,
 * med én recompute på slutten (i stedet for blur/umiddelbar persist).
 */
export function useSaveUpsellDraft() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: SaveUpsellDraftInput) => {
      let previousBillingMode: UpsellBillingMode | undefined;
      let billingModeChanged = false;

      if (input.containerPatch?.billing_mode !== undefined) {
        const { data: current, error: fetchErr } = await supabase
          .from("upsells")
          .select("billing_mode, added_to_invoice_id")
          .eq("id", input.upsell_id)
          .maybeSingle();
        if (fetchErr) throw fetchErr;
        previousBillingMode = current?.billing_mode as UpsellBillingMode | undefined;
        billingModeChanged = previousBillingMode !== input.containerPatch.billing_mode;
        if (billingModeChanged) {
          await assertBillingModeChangeAllowed(
            input.upsell_id,
            current?.added_to_invoice_id ?? null,
          );
        }
      }

      if (input.containerPatch && Object.keys(input.containerPatch).length > 0) {
        const { error } = await supabase
          .from("upsells")
          .update(input.containerPatch)
          .eq("id", input.upsell_id);
        if (error) throw error;
      }

      for (const line of input.inserts) {
        const { error } = await supabase.from("budget_lines").insert({
          project_id: input.project_id,
          upsell_id: input.upsell_id,
          phase: line.phase,
          description: line.description,
          cost_type: line.cost_type,
          unit: line.unit,
          quantity: line.quantity ?? 0,
          quantity_hours: line.quantity_hours ?? 0,
          amount: line.amount ?? 0,
          estimated_cost: line.estimated_cost ?? 0,
          template_id: line.template_id,
          rate_basis_hourly: line.rate_basis_hourly ?? null,
        });
        if (error) throw error;
      }

      for (const u of input.updates) {
        const { error } = await supabase
          .from("budget_lines")
          .update(u.patch)
          .eq("id", u.budget_line_id);
        if (error) throw error;
      }

      for (const id of input.deleteIds) {
        const { error } = await supabase.from("budget_lines").delete().eq("id", id);
        if (error) throw error;
      }

      await recomputeUpsellAmount(input.upsell_id);
      await recomputeProjectTotal(input.project_id);

      if (billingModeChanged) {
        await logProjectEvent({
          projectId: input.project_id,
          userId: user?.id ?? null,
          eventType: "upsell_billing_mode_changed",
          entityType: "upsell",
          entityId: input.upsell_id,
          payload: {
            old_billing_mode: previousBillingMode ?? "fixed",
            new_billing_mode: input.containerPatch?.billing_mode,
          },
        });
      }
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      if (vars.containerPatch?.billing_mode !== undefined) {
        await invalidateFor(qc, "project_events", { projectId: vars.project_id });
      }
      toast.success("Mersalg lagret");
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kunne ikke lagre mersalg: ${(err as Error).message}`);
    },
  });
}
